// src/screens/pass/PassSuccessView.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Container,
  Group,
  Modal,
  Paper,
  Space,
  Text,
  Title,
} from "@mantine/core";
import Confetti from "react-confetti";
import QRCode from "react-qr-code";

import { addToGoogleWallet } from "../../atoms/passNetwork";
import { CONFIG } from "../../atoms/config";

function formatCurrency(value) {
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${Number(value || 0).toFixed(2)}`;
  }
}

function formatDateRange(arrivalDate, nights) {
  try {
    const start = new Date(arrivalDate);
    const end = new Date(start);
    end.setDate(end.getDate() + Math.max(0, Number(nights || 0)));

    const fmt = new Intl.DateTimeFormat("en-AU", { dateStyle: "medium" });
    if (Number(nights || 0) < 1) return fmt.format(start);
    return `${fmt.format(start)} – ${fmt.format(end)}`;
  } catch {
    return "";
  }
}

function detectPlatform() {
  const ua = (navigator.userAgent || "").toLowerCase();
  const isAndroid = ua.includes("android");
  const isIOS =
    ua.includes("iphone") ||
    ua.includes("ipad") ||
    ua.includes("ipod") ||
    (ua.includes("mac") && "ontouchend" in document);

  return { isIOS, isAndroid };
}

function buildApplePkpassUrl(passId) {
  const base = String(CONFIG.apiUrl || "").trim();
  if (!base) return "";

  const join = base.includes("?") ? "&" : "?";

  return `${base}${join}cmd=generate-apple-wallet-wc-pass-web&pass_id=${encodeURIComponent(
    String(passId)
  )}`;
}

const PRINT_BASE = "https://pass.smartlockbooking.com";
const PRINT_API_KEY = "0HPhhdPztbcmSp4gq00kn-atVKcIE9HoHZizu1O0SrE";

export default function PassSuccessView({
  siteId,
  name,
  phoneE164,
  registrationNumber,
  nights,
  totalPaid,
  arrivalDate,
  siteTitle,
  passCode,
  passId,
  onViewTerms,
  onDone,
  onExtendPass,
}) {
  const [showConfetti, setShowConfetti] = useState(true);
  const [addingWallet, setAddingWallet] = useState(false);
  const [walletError, setWalletError] = useState("");

  // ✅ printing state (same phases as RN)
  const [printOpen, setPrintOpen] = useState(false);
  const [printPhase, setPrintPhase] = useState("loading"); // loading | received | success | failure
  const [printErr, setPrintErr] = useState("");
  const [printConfetti, setPrintConfetti] = useState(false);
  const printStartMsRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setShowConfetti(false), 1800);
    return () => clearTimeout(t);
  }, []);

  const qrUrl = useMemo(() => {
    if (!passCode) return "";
    return `https://pass.wc.slbk.au/upass/${passCode}`;
  }, [passCode]);

  const dateRangeText = useMemo(
    () => formatDateRange(arrivalDate, nights),
    [arrivalDate, nights]
  );

  const onAddToWallet = async () => {
    setWalletError("");

    const id = Number(passId || 0);
    if (!id) {
      setWalletError("Missing pass id. Please try again.");
      return;
    }

    const { isIOS, isAndroid } = detectPlatform();
    setAddingWallet(true);

    try {
      if (isIOS) {
        const url = buildApplePkpassUrl(id);
        if (!url) throw new Error("Missing CONFIG.apiUrl for Wallet download.");
        window.location.href = url;
        return;
      }

      if (isAndroid) {
        const res = await addToGoogleWallet({ passId: id });
        const url =
          res?.data?.url ||
          res?.data?.save_url ||
          res?.data?.link ||
          res?.url;

        if (!url || typeof url !== "string" || !url.startsWith("http")) {
          throw new Error("Invalid Google Wallet URL");
        }

        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      setWalletError("Wallet is only available on iOS or Android devices.");
    } catch (e) {
      console.error("Add to wallet failed:", e);
      setWalletError(e?.message || "Unable to add to wallet right now.");
    } finally {
      setAddingWallet(false);
    }
  };

  // ✅ Print (mirrors RN enqueuePrint)
  async function enqueuePrint() {
    const start = Date.now();
    printStartMsRef.current = start;

    setPrintErr("");
    setPrintPhase("loading");
    setPrintConfetti(false);
    setPrintOpen(true);

    try {
      if (!siteId) throw new Error("Missing siteId");
      if (!passCode) throw new Error("Missing pass code");

      const url = `${PRINT_BASE}/${encodeURIComponent(String(siteId))}/enqueue`;

      const payload = {
        data: {
          passHolder: String(name || "").trim() ? name : "Unnamed camper",
          phoneNumber: phoneE164 || "—",
          vehicle: String(registrationNumber || "").trim()
            ? registrationNumber
            : "—",
          amountPaid: formatCurrency(totalPaid),
          duration:
            Number(nights || 0) === 1
              ? "1 Night"
              : `${Number(nights || 0)} Nights`,
          dates: dateRangeText || "—",
          bookingCode: passCode,
        },
      };

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": PRINT_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch {}

      if (!res.ok) {
        const msg = json?.error || json?.message || text || `HTTP ${res.status}`;
        throw new Error(msg);
      }

      // received
      setPrintPhase("received");

      // enforce min 5s before success animation
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 5000 - elapsed);

      window.setTimeout(() => {
        setPrintPhase("success");
        setPrintConfetti(true);

        window.setTimeout(() => {
          setPrintOpen(false);
          setPrintConfetti(false);
        }, 1250);
      }, remaining);
    } catch (e) {
      setPrintErr(e?.message || String(e));
      setPrintPhase("failure");

      window.setTimeout(() => {
        setPrintOpen(false);
      }, 2500);
    }
  }

  const printTitle =
    printPhase === "loading"
      ? "Sending to printer…"
      : printPhase === "received"
      ? "Received by printer"
      : printPhase === "success"
      ? "Done"
      : "Couldn’t print";

  const printSub =
    printPhase === "loading"
      ? "Hang tight, this should only take a moment."
      : printPhase === "received"
      ? "Printing now…"
      : printPhase === "success"
      ? "Receipt queued successfully."
      : printErr || "Something went wrong.";

  const printTone =
    printPhase === "failure"
      ? "red"
      : printPhase === "success"
      ? "green"
      : "dimmed";

  const canPrint = Boolean(siteId && passCode);

  return (
    <Container size={700} px="md" style={{ minHeight: "100vh", paddingBottom: 60 }}>
      {showConfetti ? <Confetti recycle={false} /> : null}

      <Space h={24} />

      <Title order={2} ta="center">
        Your Pass Details
      </Title>

      <Space h="lg" />

      <Paper radius="xl" p="lg" withBorder style={{ overflow: "hidden" }}>
        <div
          style={{
            background: "linear-gradient(90deg, #004d45, #00645c, #007869)",
            color: "white",
            borderRadius: 16,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <Text fw={800}>{siteTitle || "West Coast Camping"}</Text>
          <Text size="sm" style={{ opacity: 0.9 }}>
            Pass Code: {passCode || "—"}
          </Text>
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          {qrUrl ? (
            <div style={{ background: "white", padding: 12, borderRadius: 12 }}>
              <QRCode value={qrUrl} size={130} />
            </div>
          ) : (
            <Text c="dimmed">QR unavailable</Text>
          )}
        </div>

        <Space h="md" />

        <Group justify="space-between" align="flex-start">
          <div>
            <Text size="xs" c="dimmed">
              Name
            </Text>
            <Text fw={700}>{name || "Unnamed camper"}</Text>
          </div>

          <div>
            <Text size="xs" c="dimmed">
              Vehicle rego
            </Text>
            <Text fw={700}>{registrationNumber || "—"}</Text>
          </div>

          <div>
            <Text size="xs" c="dimmed">
              Paid
            </Text>
            <Text fw={800}>{formatCurrency(totalPaid)}</Text>
          </div>
        </Group>

        <Space h="sm" />

        <Group justify="space-between" align="flex-start">
          <div>
            <Text size="xs" c="dimmed">
              Phone
            </Text>
            <Text fw={700}>{phoneE164 || "—"}</Text>
          </div>

          <div>
            <Text size="xs" c="dimmed">
              Dates
            </Text>
            <Text fw={700}>{dateRangeText || "—"}</Text>
          </div>
        </Group>

        <Space h="md" />

        <Text size="sm" c="dimmed" ta="center">
          You’re all set for your stay. Please keep this pass handy while on-site.
        </Text>

        <Space h="lg" />

        {walletError ? (
          <Text c="red" size="sm" ta="center" mb="sm">
            {walletError}
          </Text>
        ) : null}

        <Button fullWidth size="md" onClick={onAddToWallet} loading={addingWallet}>
          Add to Wallet
        </Button>

        <Space h="sm" />

        <Group grow>
          <Button variant="light" onClick={onExtendPass}>
            Extend
          </Button>

          <Button variant="light" onClick={enqueuePrint} disabled={!canPrint}>
            Print receipt
          </Button>
        </Group>

        <Button variant="subtle" fullWidth mt="sm" onClick={onDone}>
          Back to start
        </Button>

        {/* <Button variant="light" onClick={onViewTerms}>Terms</Button> */}
      </Paper>

      <Modal
        opened={printOpen}
        onClose={() => setPrintOpen(false)}
        centered
        withCloseButton={false}
        radius="lg"
        overlayProps={{ backgroundOpacity: 0.35, blur: 2 }}
      >
        {printConfetti ? <Confetti recycle={false} numberOfPieces={120} /> : null}

        <div style={{ textAlign: "center", padding: 6 }}>
          {printPhase === "loading" || printPhase === "received" ? (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
              <div className="mantine-Loader-root" />
            </div>
          ) : null}

          <Title order={4} mb={6}>
            {printTitle}
          </Title>
          <Text c={printTone} size="sm">
            {printSub}
          </Text>
        </div>
      </Modal>
    </Container>
  );
}
